import { NavLink, Outlet } from 'react-router-dom'
import { CurrencyProvider } from '../components/common/Money'
import { useAuthListener, useIsSignedIn, useSignOut } from '../hooks/useAuth'
import { useFarmSettings } from '../hooks/useFarm'
import { isNoFarm, useSessionInfo } from '../hooks/useSessionInfo'
import { FarmSetup } from '../pages/FarmSetup'
import { SignIn } from '../pages/SignIn'

const TITLE = 'Poultry Layer Farm Management'

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/entry', label: 'Daily entry' },
  { to: '/batches', label: 'Batches' },
  { to: '/costs', label: 'Costs' },
  { to: '/sales', label: 'Sales' },
  { to: '/settings', label: 'Settings' },
]

/**
 * Application shell: navigation and the signed-in context.
 *
 * Desktop-first — the farm enters a day's data on a laptop at the end of the
 * day, in dense grids, not on a phone (§2).
 */
export function AppShell() {
  useAuthListener()
  const signedIn = useIsSignedIn()
  const session = useSessionInfo()
  const signOut = useSignOut()
  // Name, currency and number format all come from the farm's settings (§11.1).
  const farm = useFarmSettings(session.isSuccess)

  if (!signedIn) {
    return (
      <div className="app-shell">
        <header className="app-header">
          <strong>{TITLE}</strong>
        </header>
        <main>
          <SignIn />
        </main>
      </div>
    )
  }

  const signOutButton = (
    <button type="button" className="link-button" onClick={() => signOut.mutate()}>
      Sign out
    </button>
  )

  // Signed in with no farm yet: first-run setup, and nothing else to navigate to.
  if (session.isError && isNoFarm(session.error)) {
    return (
      <div className="app-shell">
        <header className="app-header">
          <strong>{TITLE}</strong>
          <span className="session">{signOutButton}</span>
        </header>
        <main>
          <FarmSetup />
        </main>
      </div>
    )
  }

  return (
    <CurrencyProvider code={farm.data?.currencyCode ?? 'XXX'} locale={farm.data?.locale}>
      <div className="app-shell">
        <header className="app-header">
          <strong>{TITLE}</strong>
          <nav aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? 'active' : undefined)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <span className="session">
            {session.isPending
              ? 'Resolving farm…'
              : session.isError
                ? 'Session could not be resolved'
                : (farm.data?.name ?? 'Loading farm…')}
            {signOutButton}
          </span>
        </header>

        <main>
          {session.isError ? (
            <p role="alert" className="error">
              {session.error.message}
            </p>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </CurrencyProvider>
  )
}
